/**
 * mdbImport.js - Microsoft Access (.mdb) import engine for OptimalAvailability Studio
 * Handles importing equipment and failure data from Access databases
 * Supports .mdb binary files (with fallback to CSV export) and CSV/XML from Access
 *
 * @module engine/mdbImport
 * @exports MdbImport
 */

class MdbImport {
  /**
   * Access table name mappings to OptimalAvailability entity types
   * Similar to SAP/Maximo table mapping patterns
   * @static
   */
  static TABLE_MAPPINGS = {
    equipment: [
      'Equipment',
      'Assets',
      'Machines',
      'Facilities',
      'Devices',
      'EQUIPMENT',
      'ASSETS',
    ],
    failureHistory: [
      'FailureHistory',
      'Failures',
      'Incidents',
      'Breakdowns',
      'WorkOrders',
      'FAILURE_HISTORY',
      'FAILURES',
      'WORK_ORDERS',
    ],
    maintenanceTasks: [
      'MaintenanceTasks',
      'Tasks',
      'Activities',
      'Jobs',
      'MAINTENANCE_TASKS',
      'TASKS',
    ],
    spareParts: [
      'SpareParts',
      'Parts',
      'Inventory',
      'PartsMaster',
      'SPARE_PARTS',
      'PARTS',
    ],
    technicians: [
      'Technicians',
      'Staff',
      'Personnel',
      'Employees',
      'Users',
      'TECHNICIANS',
      'STAFF',
    ],
  };

  /**
   * Common Access field type indicators and their conversions
   * @static
   */
  static ACCESS_FIELD_TYPES = {
    DATE: ['DateTime', 'Date', 'Timestamp', 'date_field'],
    NUMBER: ['Number', 'Integer', 'Long', 'Double', 'Single', 'Currency'],
    TEXT: ['Text', 'String', 'Memo', 'VarChar'],
    BOOLEAN: ['Yes/No', 'Boolean', 'Bit'],
    GUID: ['GUID', 'UUID'],
  };

  /**
   * Import an MDB file buffer
   * Since pure JavaScript MDB parsing is limited, provide fallback strategy:
   * 1. Try to extract basic header and table names
   * 2. Recommend CSV export from Access for actual data import
   *
   * @param {Buffer|Uint8Array} fileBuffer - MDB file buffer
   * @returns {Promise<Object>} { success: boolean, tables: string[], message: string, recommendation: string }
   */
  static async importMDB(fileBuffer) {
    try {
      // Check for MDB file signature (Access 2000 format)
      const header = this._readMDBHeader(fileBuffer);

      if (!header.isValid) {
        return {
          success: false,
          tables: [],
          message: 'Invalid MDB file format',
          recommendation: 'Please export the Access database as CSV and import using the CSV importer',
        };
      }

      // Extract table names from MDB structure (limited capability)
      const tables = this._extractTableNamesFromMDB(fileBuffer);

      return {
        success: true,
        tables: tables || [],
        message: `Found Access ${header.version} database with ${tables.length || 'unknown'} tables`,
        recommendation:
          'For reliable data import, export the following tables from Access as CSV files: ' +
          tables.slice(0, 5).join(', '),
        version: header.version,
      };
    } catch (error) {
      return {
        success: false,
        tables: [],
        message: `Error reading MDB file: ${error.message}`,
        recommendation: 'Export the Access database as CSV files and import them instead',
      };
    }
  }

  /**
   * Import CSV content from Access export
   * Handles Access-specific formatting (dates, memo fields, Yes/No booleans)
   *
   * @param {string} csvContent - CSV content from Access export
   * @param {string} tableName - Logical table name (equipment, failureHistory, etc.)
   * @param {Object} options - Import options
   * @param {string} options.delimiter - CSV delimiter (default: ',')
   * @param {boolean} options.hasHeader - First row is header (default: true)
   * @param {Object} options.fieldMapping - Map CSV columns to entity fields
   * @returns {Object} { success: boolean, data: Array<Object>, headers: string[], errors: string[] }
   */
  static importAccessCSV(csvContent, tableName, options = {}) {
    const opts = {
      delimiter: options.delimiter || ',',
      hasHeader: options.hasHeader !== false,
      fieldMapping: options.fieldMapping || {},
    };

    const errors = [];
    const rows = [];
    let headers = [];

    try {
      // Parse CSV (basic implementation; handles quoted fields)
      const lines = csvContent.split('\n').filter(line => line.trim());

      if (lines.length === 0) {
        return {
          success: false,
          data: [],
          headers: [],
          errors: ['CSV file is empty'],
        };
      }

      // Extract headers
      if (opts.hasHeader) {
        headers = this._parseCSVLine(lines[0], opts.delimiter);
      }

      // Parse data rows
      const startLine = opts.hasHeader ? 1 : 0;
      for (let i = startLine; i < lines.length; i++) {
        const line = lines[i];
        if (!line.trim()) continue;

        try {
          const values = this._parseCSVLine(line, opts.delimiter);
          const row = {};

          headers.forEach((header, idx) => {
            const fieldName = opts.fieldMapping[header] || this._normalizeFieldName(header);
            const value = values[idx];

            // Convert Access-specific formats
            row[fieldName] = this._convertAccessValue(value, header);
          });

          rows.push(row);
        } catch (rowError) {
          errors.push(`Row ${i + 1}: ${rowError.message}`);
        }
      }

      return {
        success: errors.length === 0,
        data: rows,
        headers: headers.map(h => opts.fieldMapping[h] || this._normalizeFieldName(h)),
        errors,
        recordCount: rows.length,
      };
    } catch (error) {
      return {
        success: false,
        data: [],
        headers,
        errors: [`CSV parsing error: ${error.message}`],
      };
    }
  }

  /**
   * Detect if content is MDB binary, CSV from Access, or XML from Access
   *
   * @param {Buffer|string} content - File content
   * @param {string} filename - Original filename for hints
   * @returns {Object} { format: 'mdb'|'csv'|'xml'|'unknown', confidence: number (0-1) }
   */
  static detectAccessFormat(content, filename = '') {
    // Check if Buffer/binary
    if (content instanceof Buffer || content instanceof Uint8Array) {
      const view = new Uint8Array(content);

      // MDB file signature (Access 2000): 00 01 00 00
      if (view.length > 4 && view[0] === 0 && view[1] === 1 && view[2] === 0 && view[3] === 0) {
        return { format: 'mdb', confidence: 0.95 };
      }

      // Check for older Access 97: AC DB
      if (view.length > 2 && view[0] === 0xac && view[1] === 0xdb) {
        return { format: 'mdb', confidence: 0.9 };
      }

      return { format: 'unknown', confidence: 0 };
    }

    // String content
    if (typeof content === 'string') {
      const sample = content.substring(0, 500);

      // Check for XML (Access XML export)
      if (sample.includes('<?xml') || sample.includes('<dataroot>') || sample.includes('<Schema')) {
        return { format: 'xml', confidence: 0.85 };
      }

      // Check for CSV
      const lines = sample.split('\n');
      const firstLine = lines[0];

      // Heuristic: CSV if has commas and no XML indicators
      if (
        (firstLine.includes(',') || firstLine.includes('\t')) &&
        !sample.includes('<?xml') &&
        !sample.includes('<')
      ) {
        return { format: 'csv', confidence: 0.8 };
      }
    }

    return { format: 'unknown', confidence: 0 };
  }

  /**
   * Get mapping of common Access table names to OptimalAvailability entity types
   *
   * @returns {Object} Mapping of detected table names to entity categories
   */
  static getAccessTableMapping() {
    return {
      ...MdbImport.TABLE_MAPPINGS,
      description:
        'Common Access table name mappings. Use to identify and categorize imported tables.',
    };
  }

  /**
   * Auto-detect format and import content
   * Intelligently handles MDB, CSV, or XML
   *
   * @param {Buffer|string} content - File content
   * @param {string} filename - Original filename
   * @param {Object} options - Import options
   * @returns {Promise<Object>} Parsed data and metadata
   */
  static async autoImport(content, filename, options = {}) {
    const detection = this.detectAccessFormat(content, filename);

    if (detection.confidence < 0.5) {
      return {
        success: false,
        message: 'Unable to detect Access file format with confidence',
        format: detection.format,
        recommendation:
          'Ensure the file is a valid MDB, CSV, or XML export from Microsoft Access',
      };
    }

    if (detection.format === 'mdb') {
      return await this.importMDB(content);
    }

    if (detection.format === 'csv') {
      // For CSV, need table name from filename or options
      const tableName = options.tableName || this._inferTableFromFilename(filename);

      return this.importAccessCSV(
        content,
        tableName,
        options
      );
    }

    if (detection.format === 'xml') {
      return this._importAccessXML(content, options);
    }

    return {
      success: false,
      message: 'Unsupported Access file format',
      format: detection.format,
    };
  }

  /**
   * Private helper: Read and validate MDB file header
   * @private
   */
  static _readMDBHeader(buffer) {
    const view = new Uint8Array(buffer);

    if (buffer.length < 20) {
      return { isValid: false, version: null };
    }

    // Check for Access 2000+ format (00 01 00 00)
    if (view[0] === 0 && view[1] === 1 && view[2] === 0 && view[3] === 0) {
      return { isValid: true, version: 'Access 2000 or later' };
    }

    // Check for Access 97 format (AC DB)
    if (view[0] === 0xac && view[1] === 0xdb) {
      return { isValid: true, version: 'Access 97' };
    }

    return { isValid: false, version: null };
  }

  /**
   * Private helper: Extract table names from MDB binary structure
   * This is a limited implementation; real parsing requires full MDB format knowledge
   * @private
   */
  static _extractTableNamesFromMDB(buffer) {
    // MDB parsing is complex. This is a simplified approach that looks for table name patterns
    // In a production system, consider using a proper library or recommending CSV export
    const tables = [];

    try {
      const view = new Uint8Array(buffer);
      const decoder = new TextDecoder('utf-8', { fatal: false });

      // Look for ASCII strings that might be table names
      // This is heuristic and won't catch all tables, but gives user some info
      let currentString = '';

      for (let i = 0; i < Math.min(buffer.length, 100000); i++) {
        const byte = view[i];

        // ASCII printable range
        if (byte >= 32 && byte <= 126) {
          currentString += String.fromCharCode(byte);
        } else {
          if (currentString.length > 3 && currentString.length < 50) {
            // Check if looks like a table name (no spaces at edges, reasonable length)
            if (this._looksLikeTableName(currentString)) {
              tables.push(currentString);
            }
          }
          currentString = '';
        }
      }

      return [...new Set(tables)].slice(0, 20); // Return unique, limit to 20
    } catch (e) {
      return [];
    }
  }

  /**
   * Private helper: Check if string looks like a table name
   * @private
   */
  static _looksLikeTableName(str) {
    // Table names typically: start with letter, contain letters/numbers/underscores
    // Don't contain special chars except underscore
    const pattern = /^[A-Za-z][A-Za-z0-9_]{2,}$/;
    return pattern.test(str) && !str.includes('system');
  }

  /**
   * Private helper: Parse a CSV line handling quoted fields
   * @private
   */
  static _parseCSVLine(line, delimiter = ',') {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          // Escaped quote
          current += '"';
          i++; // Skip next quote
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
        }
      } else if (char === delimiter && !inQuotes) {
        // Field delimiter
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }

    result.push(current.trim());
    return result;
  }

  /**
   * Private helper: Normalize field names to camelCase
   * @private
   */
  static _normalizeFieldName(fieldName) {
    return fieldName
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '')
      .replace(/_+/g, '_');
  }

  /**
   * Private helper: Convert Access-specific value formats
   * @private
   */
  static _convertAccessValue(value, fieldName) {
    if (!value || value === '' || value === 'NULL') {
      return null;
    }

    const lower = value.toLowerCase();
    const fieldLower = fieldName.toLowerCase();

    // Yes/No fields
    if (lower === 'yes' || lower === 'true' || lower === '-1' || lower === '1') {
      return true;
    }
    if (lower === 'no' || lower === 'false' || lower === '0') {
      return false;
    }

    // Date fields
    if (fieldLower.includes('date') || fieldLower.includes('time')) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toISOString();
        }
      } catch (e) {
        // Not a valid date
      }
    }

    // Numeric fields
    if (fieldLower.includes('rate') || fieldLower.includes('cost') || fieldLower.includes('hours')) {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        return num;
      }
    }

    // Return as-is for text
    return value;
  }

  /**
   * Private helper: Infer table name from filename
   * @private
   */
  static _inferTableFromFilename(filename) {
    const base = filename.split('.')[0].toLowerCase();

    // Try to match against known table types
    for (const [type, names] of Object.entries(MdbImport.TABLE_MAPPINGS)) {
      if (names.some(n => base.includes(n.toLowerCase()))) {
        return type;
      }
    }

    return base; // Return as-is if no match
  }

  /**
   * Private helper: Import Access XML format
   * @private
   */
  static _importAccessXML(xmlContent, options = {}) {
    // XML imports from Access typically follow a DataSet format
    // This is a simplified parser for basic XML tables
    try {
      // Basic XML parsing without external library
      const parser = new DOMParser();
      const doc = parser.parseFromString(xmlContent, 'application/xml');

      if (doc.getElementsByTagName('parsererror').length > 0) {
        return {
          success: false,
          data: [],
          message: 'Invalid XML format',
          errors: ['XML parsing failed'],
        };
      }

      // Look for common Access XML patterns (rows/items/records)
      const rowElements =
        doc.getElementsByTagName('row').length > 0
          ? doc.getElementsByTagName('row')
          : doc.getElementsByTagName('Record').length > 0
            ? doc.getElementsByTagName('Record')
            : doc.getElementsByTagName('Item');

      const data = [];
      const fieldNames = new Set();

      for (let i = 0; i < rowElements.length; i++) {
        const row = {};
        const element = rowElements[i];
        const children = element.children;

        for (let j = 0; j < children.length; j++) {
          const field = children[j];
          const fieldName = this._normalizeFieldName(
            field.tagName || field.getAttribute('name') || `field_${j}`
          );
          const value = field.textContent;

          row[fieldName] = this._convertAccessValue(value, fieldName);
          fieldNames.add(fieldName);
        }

        if (Object.keys(row).length > 0) {
          data.push(row);
        }
      }

      return {
        success: true,
        data,
        headers: Array.from(fieldNames),
        recordCount: data.length,
        format: 'xml',
      };
    } catch (error) {
      return {
        success: false,
        data: [],
        message: `XML import error: ${error.message}`,
        errors: [error.message],
      };
    }
  }
}

export default MdbImport;
